import "./ShippingOrderEditModalBasicData.scss"

import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Dayjs } from "utils/dayjs"
import cn from "classnames"

import { Grid } from "components/Grid"
import DeliveryTerm from "models/DeliveryTerm"
import { Guid } from "@/types"
import FormField from "components/FormField/FormField"
import CustomError from "models/CustomError"
import ValidationService from "services/ValidationService"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import SelectDeliveryTerm from "components/Select/SelectDeliveryTerm"
import { IShippingOrderHeaderDataChange } from "./hooks/useShippingOrderEditModal"
import InputDateTime from "components/InputDateTime/InputDateTime"
import { EVENT_STEP, INPUT_MAX_LENGTH_LARGE } from "config/constants"
import Input from "components/Input/Input"
import { TenantWarehouse } from "models/TenantWarehouse"
import Select from "components/Select/Select"
import Text from "components/Text/Text"
import Checkbox from "components/Checkbox/Checkbox"
import TextSection from "components/TextSection/TextSection"

interface IShippingOrderEditModalBasicDataProps {
    deliveryTerms?: DeliveryTerm[]
    deliveryTermId?: Guid
    isDeliveryTermRequired: boolean
    tenantWarehouses?: TenantWarehouse[]
    responsibleWarehouseId?: Guid
    isDepartureAndArrivalDateRequired: boolean
    departureDate: Dayjs | null
    arrivalDate: Dayjs | null
    personInCharge: string
    personInChargePhone: string
    deliveryNoteHeaderText?: string
    deliveryNoteFooterText?: string
    deliveryNotePrintFeatures: boolean
    showDeliveryNoteTextSection?: boolean
    note: string
    isSubmitted: boolean
    isDisabled: boolean
    validationErrors: { [x: string]: CustomError }
    onChange(params: Partial<IShippingOrderHeaderDataChange>): void
}

const ShippingOrderEditModalBasicData: FC<IShippingOrderEditModalBasicDataProps> = ({
    deliveryTerms,
    deliveryTermId,
    isDeliveryTermRequired,
    tenantWarehouses,
    responsibleWarehouseId,
    isDepartureAndArrivalDateRequired,
    departureDate,
    arrivalDate,
    personInCharge: personInChargeInit,
    personInChargePhone: personInChargePhoneInit,
    deliveryNoteHeaderText,
    deliveryNoteFooterText,
    deliveryNotePrintFeatures,
    showDeliveryNoteTextSection = false,
    note: noteInit,
    isSubmitted,
    isDisabled,
    validationErrors,
    onChange,
}) => {
    const { t } = useTranslation()
    const [note, setNote] = useState(noteInit)
    const [personInCharge, setPersonInCharge] = useState(personInChargeInit)
    const [personInChargePhone, setPersonInChargePhone] = useState(personInChargePhoneInit)

    useEffect(() => {
        setNote(noteInit)
    }, [noteInit])

    useEffect(() => {
        setPersonInCharge(personInChargeInit)
    }, [personInChargeInit])

    useEffect(() => {
        setPersonInChargePhone(personInChargePhoneInit)
    }, [personInChargePhoneInit])

    return (
        <>
            <Grid columns="1fr 1fr" gap="2.4rem">
                <Grid
                    columns="1fr 1fr"
                    gap="1.6rem 2.4rem">
                    <FormField
                        label={t('shippingOrders.termsOfDelivery')}
                        optional={!isDeliveryTermRequired}>
                        <SelectDeliveryTerm
                            deliveryTerms={deliveryTerms}
                            selectedDeliveryTermId={deliveryTermId}
                            onChange={deliveryTerm => onChange({ deliveryTermId: deliveryTerm?.termId || null })}
                            isDisabled={isDisabled}
                            isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'deliveryTermId')}
                            isInModal
                        />
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'deliveryTermId') &&
                            <ValidationMessage>{validationErrors.deliveryTermId.message}</ValidationMessage>
                        }
                    </FormField>
                    <FormField
                        label={t('shippingOrders.responsibleWarehouse')}
                        optional>
                        <Select
                            options={tenantWarehouses?.map(warehouse => ({ label: warehouse.description, value: warehouse.id }))}
                            value={responsibleWarehouseId}
                            onChange={option => onChange({ responsibleWarehouseId: option?.value || null })}
                            isDisabled={isDisabled}
                            isClearable
                            isInModal
                        />
                    </FormField>
                    <FormField
                        label={t('shippingOrders.departure')}
                        optional={!isDepartureAndArrivalDateRequired}>
                        <InputDateTime
                            type="datetime"
                            date={departureDate}
                            defaultActiveStartDate={departureDate ? undefined : arrivalDate?.toDate()}
                            utc
                            minutesStep={EVENT_STEP}
                            onDateChange={date => onChange({ departureDate: date })}
                            disabled={isDisabled}
                            isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'departureDate')}
                            clearButton
                        />
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'departureDate') &&
                            <ValidationMessage>{validationErrors.departureDate.message}</ValidationMessage>
                        }
                    </FormField>
                    <FormField
                        label={t('shippingOrders.arrival')}
                        optional={!isDepartureAndArrivalDateRequired}>
                        <InputDateTime
                            type="datetime"
                            date={arrivalDate}
                            defaultActiveStartDate={arrivalDate ? undefined : departureDate?.toDate()}
                            utc
                            minutesStep={EVENT_STEP}
                            onDateChange={date => onChange({ arrivalDate: date })}
                            disabled={isDisabled}
                            isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'arrivalDate')}
                            clearButton
                        />
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'arrivalDate') &&
                            <ValidationMessage>{validationErrors.arrivalDate.message}</ValidationMessage>
                        }
                    </FormField>
                    <FormField
                        label={t('reservation.contactPersonAtDeliveryAddress')}
                        optional>
                        <Input
                            value={personInCharge}
                            onChange={e => setPersonInCharge(e.target.value)}
                            onBlur={e => onChange({ personInCharge: e.target.value })}
                            disabled={isDisabled}
                        />
                    </FormField>
                    <FormField
                        label={t('reservation.contactPersonAtDeliveryAddressPhone')}
                        optional>
                        <Input
                            value={personInChargePhone}
                            onChange={e => setPersonInChargePhone(e.target.value)}
                            onBlur={e => onChange({ personInChargePhone: e.target.value })}
                            disabled={isDisabled}
                            placeholder={t('reservation.contactPersonAtDeliveryAddressPhonePlaceholder')}
                        />
                    </FormField>
                </Grid>
                <FormField
                    label={t('shippingOrders.shippingInstructions')}
                    optional>
                    <Input
                        type="textarea"
                        value={note}
                        data-testid="shipping-order-note"
                        onChange={e => setNote(e.target.value)}
                        onBlur={e => onChange({ note: e.target.value })}
                        disabled={isDisabled}
                        className="shipping-order-edit-modal-basic-data__note"
                        maxLength={INPUT_MAX_LENGTH_LARGE}
                    />
                </FormField>
            </Grid>
            {showDeliveryNoteTextSection &&
                <>
                    <Text type="h4" className="border-top-grey-100 mt-4 mb-2 pt-3">{t('reservation.deliveryNoteText')}</Text>
                    <Grid columns="minmax(0, 1fr) minmax(0, 1fr)" gap="2.4rem">
                        <TextSection
                            title={t('reservation.headerSection')}
                            popupTitle={t('reservation.deliveryNoteHeaderSectionPopupTitle')}
                            text={deliveryNoteHeaderText ?? ""}
                            onChange={value => onChange({ deliveryNoteHeaderText: value })}
                            formattingRuleLabel={t("textModal.noRule")}
                            textVariables
                            bodyClassName={cn(
                                "bg-grey-50 round-sm",
                                { "p-2": !deliveryNoteHeaderText, "h-100": deliveryNoteHeaderText },
                            )}
                        />
                        <TextSection
                            title={t('reservation.footerSection')}
                            popupTitle={t('reservation.deliveryNoteFooterSectionPopupTitle')}
                            text={deliveryNoteFooterText ?? ""}
                            onChange={value => onChange({ deliveryNoteFooterText: value })}
                            formattingRuleLabel={t("textModal.noRule")}
                            textVariables
                            bodyClassName={cn(
                                "bg-grey-50 round-sm",
                                { "p-2": !deliveryNoteFooterText, "h-100": deliveryNoteFooterText },
                            )}
                        />
                    </Grid>
                    <div className="mt-4 mb-3">
                        <Checkbox
                            label={t('shippingOrders.printFeaturesOnDeliveryNote')}
                            checked={deliveryNotePrintFeatures}
                            disabled={isDisabled}
                            onChange={() => onChange({ deliveryNotePrintFeatures: !deliveryNotePrintFeatures })}
                            testId="print-features-on-delivery-note-checkbox"
                        />
                    </div>
                </>
            }
        </>
    )
}

export default ShippingOrderEditModalBasicData
